import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  TrendingUp,
  BarChart3,
  Users,
  Briefcase,
  DollarSign,
  MapPin,
  Zap,
  ArrowUpRight,
  Download,
  RefreshCw,
  Target,
  Award,
  Building2,
  Sparkles
} from "lucide-react";

const JobSeekerMarketInsights = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchInsights();
  }, []);

  const fetchInsights = async () => {
    try {
      // Fetch market data and user-specific insights in parallel
      const [marketRes, profileRes] = await Promise.all([
        fetch(`${API}/analytics/market-insights`, { credentials: "include" }),
        fetch(`${API}/profile/jobseeker`, { credentials: "include" })
      ]);

      const marketData = marketRes.ok ? await marketRes.json() : {};
      const profile = profileRes.ok ? await profileRes.json() : {};

      // Calculate personalized insights
      const userSkills = profile.skills || [];
      const userIndustry = profile.industry || "Technology";
      const userLocation = profile.location || "";

      // Filter market data for user relevance
      const relevantJobs = await fetch(`${API}/jobs?industry=${encodeURIComponent(userIndustry)}`, {
        credentials: "include"
      }).then(r => r.ok ? r.json() : []);

      // Calculate skill demand for user's skills
      const skillDemand = {};
      relevantJobs.forEach(job => {
        (job.skills || []).forEach(skill => {
          if (userSkills.includes(skill)) {
            skillDemand[skill] = (skillDemand[skill] || 0) + 1;
          }
        });
      });

      // Calculate average salary for user's profile
      const relevantSalaries = relevantJobs
        .filter(j => j.salary_min && j.salary_max)
        .map(j => (j.salary_min + j.salary_max) / 2);
      
      const avgSalary = relevantSalaries.length > 0
        ? Math.round(relevantSalaries.reduce((a, b) => a + b, 0) / relevantSalaries.length)
        : null;

      setData({
        market: marketData,
        profile,
        relevantJobs: relevantJobs.length,
        skillDemand: Object.entries(skillDemand)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 8),
        avgSalary,
        userSkills,
        userIndustry
      });
    } catch (error) {
      console.error("Failed to fetch insights:", error);
      toast.error("Failed to load market insights");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchInsights();
    setRefreshing(false);
    toast.success("Market data refreshed");
  };

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const response = await fetch(`${API}/reports/analytics/pdf?report_type=market`, {
        credentials: "include",
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "market_insights_report.pdf";
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success("Report downloaded!");
      } else {
        toast.error("Failed to download report");
      }
    } catch (error) {
      toast.error("Download failed");
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian">
        <Sidebar />
        <main className="lg:ml-64 p-4 sm:p-6 pt-20 lg:pt-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="w-16 h-16 bg-crimson/20 flex items-center justify-center mx-auto mb-4 animate-pulse">
                <BarChart3 className="w-8 h-8 text-crimson" />
              </div>
              <p className="text-gray-400">Loading market insights...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const market = data?.market || {};
  const trendingSkills = market.trending_skills || [];

  return (
    <div className="min-h-screen bg-obsidian">
      <Sidebar />
      <main className="lg:ml-64 p-4 sm:p-6 pt-20 lg:pt-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold mb-1 sm:mb-2">
              MARKET <span className="text-crimson">INSIGHTS</span>
            </h1>
            <p className="text-gray-400 text-sm sm:text-base">
              Personalized for your skills in {data?.userIndustry || "your industry"}
            </p>
          </div>
          <div className="flex gap-2 sm:gap-3">
            <Button
              onClick={handleRefresh}
              variant="outline"
              className="border-[#333] hover:border-crimson text-sm"
              disabled={refreshing}
            >
              <RefreshCw className={`w-4 h-4 sm:mr-2 ${refreshing ? "animate-spin" : ""}`} />
              <span className="hidden sm:inline">Refresh</span>
            </Button>
            <Button
              onClick={handleDownloadPDF}
              className="bg-crimson hover:bg-crimson-hover"
              disabled={downloading}
            >
              <Download className="w-4 h-4 mr-2" />
              Download PDF
            </Button>
          </div>
        </div>

        {/* How Market Insights Works */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-obsidian-light border border-[#222] p-4 sm:p-6 mb-6 sm:mb-8"
        >
          <div className="flex items-center gap-3 mb-4 sm:mb-6">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-crimson/20 flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-crimson" />
            </div>
            <div>
              <h3 className="font-heading text-base sm:text-lg font-bold">How Market Insights Works</h3>
              <p className="text-gray-500 text-xs sm:text-sm">Personalized intelligence based on your profile</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <div className="p-3 sm:p-4 bg-gradient-to-br from-crimson/10 to-transparent border border-crimson/20 hover:border-crimson/40 transition-colors">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-crimson/20 flex items-center justify-center mb-2 sm:mb-3">
                <Target className="w-4 h-4 sm:w-5 sm:h-5 text-crimson" />
              </div>
              <h4 className="text-white font-semibold text-sm mb-1">Skills in Demand</h4>
              <p className="text-gray-400 text-xs leading-relaxed">See which of your skills are most sought after in current job postings</p>
            </div>
            
            <div className="p-3 sm:p-4 bg-gradient-to-br from-green-500/10 to-transparent border border-green-500/20 hover:border-green-500/40 transition-colors">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-500/20 flex items-center justify-center mb-2 sm:mb-3">
                <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-green-400" />
              </div>
              <h4 className="text-white font-semibold text-sm mb-1">Trending Skills</h4>
              <p className="text-gray-400 text-xs leading-relaxed">Discover in-demand skills to learn for career growth</p>
            </div>
            
            <div className="p-3 sm:p-4 bg-gradient-to-br from-blue-500/10 to-transparent border border-blue-500/20 hover:border-blue-500/40 transition-colors">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-500/20 flex items-center justify-center mb-2 sm:mb-3">
                <Building2 className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400" />
              </div>
              <h4 className="text-white font-semibold text-sm mb-1">Industry Overview</h4>
              <p className="text-gray-400 text-xs leading-relaxed">Job distribution across industries based on market data</p>
            </div>
            
            <div className="p-3 sm:p-4 bg-gradient-to-br from-yellow-500/10 to-transparent border border-yellow-500/20 hover:border-yellow-500/40 transition-colors">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-yellow-500/20 flex items-center justify-center mb-2 sm:mb-3">
                <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400" />
              </div>
              <h4 className="text-white font-semibold text-sm mb-1">Salary Insights</h4>
              <p className="text-gray-400 text-xs leading-relaxed">Average compensation for roles matching your profile</p>
            </div>
          </div>
          
          <p className="text-gray-600 text-xs mt-4 text-center">
            Data refreshed in real-time from active job postings. Add more skills to see personalized insights.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Main Content - Left 2 columns */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            {/* Quick Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-obsidian-light border border-[#222] p-4 sm:p-6"
              >
                <div className="flex items-center gap-3 mb-2 sm:mb-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-crimson/20 flex items-center justify-center flex-shrink-0">
                    <Briefcase className="w-4 h-4 sm:w-5 sm:h-5 text-crimson" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-gray-500 text-xs uppercase">Matching Jobs</p>
                    <p className="text-xl sm:text-2xl font-bold text-white">{data?.relevantJobs || 0}</p>
                  </div>
                </div>
                <p className="text-gray-500 text-xs">In your industry</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-obsidian-light border border-[#222] p-4 sm:p-6"
              >
                <div className="flex items-center gap-3 mb-2 sm:mb-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-500/20 flex items-center justify-center flex-shrink-0">
                    <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 text-green-400" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-gray-500 text-xs uppercase">Avg Salary</p>
                    <p className="text-xl sm:text-2xl font-bold text-white">
                      {data?.avgSalary ? `$${data.avgSalary.toLocaleString()}` : "N/A"}
                    </p>
                  </div>
                </div>
                <p className="text-gray-500 text-xs">For your profile</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-obsidian-light border border-[#222] p-4 sm:p-6"
              >
                <div className="flex items-center gap-3 mb-2 sm:mb-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                    <Users className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-gray-500 text-xs uppercase">Competition</p>
                    <p className="text-xl sm:text-2xl font-bold text-white">
                      {market.total_candidates || "Medium"}
                    </p>
                  </div>
                </div>
                <p className="text-gray-500 text-xs">Active job seekers</p>
              </motion.div>
            </div>

            {/* Your Skills Demand */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-obsidian-light border border-[#222] p-6"
            >
              <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                <Target className="w-5 h-5 text-crimson" />
                Your Skills in Demand
              </h3>
              
              {market?.user_skills_in_demand && market.user_skills_in_demand.length > 0 ? (
                <div className="space-y-3">
                  {market.user_skills_in_demand.map((skill, idx) => (
                    <div key={skill.skill} className="flex items-center gap-3">
                      <span className="text-gray-400 w-6">{idx + 1}.</span>
                      <div className="flex-1">
                        <div className="flex justify-between mb-1">
                          <span className="text-white font-medium">{skill.skill}</span>
                          <span className="text-crimson">{skill.demand} jobs</span>
                        </div>
                        <div className="h-2 bg-[#222] rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-crimson to-red-500"
                            style={{ width: `${Math.min(100, (skill.demand / (market.user_skills_in_demand[0]?.demand || 1)) * 100)}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : data?.skillDemand && data.skillDemand.length > 0 ? (
                <div className="space-y-3">
                  {data.skillDemand.map(([skill, count], idx) => (
                    <div key={skill} className="flex items-center gap-3">
                      <span className="text-gray-400 w-6">{idx + 1}.</span>
                      <div className="flex-1">
                        <div className="flex justify-between mb-1">
                          <span className="text-white font-medium">{skill}</span>
                          <span className="text-crimson">{count} jobs</span>
                        </div>
                        <div className="h-2 bg-[#222] rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-crimson to-red-500"
                            style={{ width: `${Math.min(100, (count / (data.skillDemand[0]?.[1] || 1)) * 100)}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>Add more skills to your profile to see demand data</p>
                  <Link to="/profile/build" className="text-crimson hover:underline mt-2 inline-block">
                    Update Profile
                  </Link>
                </div>
              )}
            </motion.div>

            {/* Trending Skills in Market */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-obsidian-light border border-[#222] p-6"
            >
              <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-green-400" />
                Trending Skills to Learn
              </h3>
              
              {market?.trending_skills_to_learn && market.trending_skills_to_learn.length > 0 ? (
                <div className="grid grid-cols-2 gap-3">
                  {market.trending_skills_to_learn.slice(0, 8).map((skill, idx) => (
                    <div
                      key={idx}
                      className="p-3 border border-[#333] flex items-center justify-between"
                    >
                      <span className="text-white">{skill.skill}</span>
                      <span className="text-xs text-gray-400">{skill.demand} jobs</span>
                    </div>
                  ))}
                </div>
              ) : trendingSkills.length > 0 ? (
                <div className="grid grid-cols-2 gap-3">
                  {trendingSkills.slice(0, 8).map((skill, idx) => {
                    const skillName = skill.skill || skill.name || skill;
                    const hasSkill = data?.userSkills?.includes(skillName);
                    
                    // Don't show skills the user already has in "to learn" section
                    if (hasSkill) return null;
                    
                    return (
                      <div
                        key={idx}
                        className="p-3 border border-[#333] flex items-center justify-between"
                      >
                        <span className="text-white">{skillName}</span>
                        <ArrowUpRight className="w-4 h-4 text-gray-500" />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>No trending skills data available</p>
                </div>
              )}
            </motion.div>
          </div>

          {/* Sidebar - Right column */}
          <div className="space-y-6">
            {/* Industry Overview */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-obsidian-light border border-[#222] p-6"
            >
              <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-crimson" />
                {data?.userIndustry || "Industry"} Overview
              </h3>
              
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-gray-400">Active Jobs</span>
                  <span className="text-white font-medium">{data?.relevantJobs || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Growth Trend</span>
                  <span className="text-green-400 font-medium flex items-center gap-1">
                    <TrendingUp className="w-4 h-4" /> +12%
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Avg Experience</span>
                  <span className="text-white font-medium">3-5 years</span>
                </div>
              </div>
            </motion.div>

            {/* Quick Actions */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-obsidian-light border border-[#222] p-6"
            >
              <h3 className="font-heading text-lg font-bold mb-4">Quick Actions</h3>
              <div className="space-y-3">
                <Link
                  to="/jobs"
                  className="flex items-center gap-3 p-3 bg-obsidian border border-[#333] hover:border-crimson/50 transition-colors"
                >
                  <Briefcase className="w-4 h-4 text-crimson" />
                  <span className="text-gray-300">Browse Matching Jobs</span>
                </Link>
                <Link
                  to="/career-insights"
                  className="flex items-center gap-3 p-3 bg-obsidian border border-[#333] hover:border-crimson/50 transition-colors"
                >
                  <Sparkles className="w-4 h-4 text-crimson" />
                  <span className="text-gray-300">Get AI Career Advice</span>
                </Link>
                <Link
                  to="/profile/build"
                  className="flex items-center gap-3 p-3 bg-obsidian border border-[#333] hover:border-crimson/50 transition-colors"
                >
                  <Award className="w-4 h-4 text-crimson" />
                  <span className="text-gray-300">Improve Your Profile</span>
                </Link>
              </div>
            </motion.div>

            {/* Upgrade CTA */}
            {user?.subscription_tier === "jobseeker_free" && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-gradient-to-br from-crimson/20 to-crimson/5 border border-crimson/30 p-6"
              >
                <div className="flex items-center gap-2 mb-3">
                  <Zap className="w-5 h-5 text-crimson" />
                  <h3 className="font-heading font-bold text-white">Go Pro</h3>
                </div>
                <p className="text-gray-400 text-sm mb-4">
                  Get priority ranking, AI feedback, and early access to new jobs.
                </p>
                <Link to="/settings">
                  <Button className="w-full bg-crimson hover:bg-crimson-hover">
                    Upgrade Now
                  </Button>
                </Link>
              </motion.div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default JobSeekerMarketInsights;
